import { Link } from "react-router-dom";

const FALLBACK_IMAGE =
  "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return ""; }
}

export default function ArticleCard({ article, variant = "default", index = 0 }) {
  const cover = article.cover_image || FALLBACK_IMAGE;

  if (variant === "featured") {
    return (
      <Link
        to={`/article/${article.slug}`}
        data-testid={`featured-article-${article.slug}`}
        className="group block animate-fade-up"
        style={{ animationDelay: `${index * 60}ms` }}
      >
        <div className="grid md:grid-cols-5 gap-8 items-center">
          <div className="md:col-span-3 rounded-2xl overflow-hidden bg-[var(--rciq-bg-tint)] aspect-[16/10]">
            <img
              src={cover}
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            />
          </div>
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <span className="tag-pill">{article.category}</span>
              <span className="text-xs text-[var(--rciq-muted)]">{formatDate(article.published_at)}</span>
            </div>
            <h2 className="font-serif-display text-3xl md:text-4xl leading-tight font-700 text-[var(--rciq-ink)] group-hover:text-[var(--rciq-teal-dark)] transition-colors">
              {article.title}
            </h2>
            <p className="mt-4 text-[var(--rciq-ink-2)] leading-relaxed">{article.excerpt}</p>
            <div className="mt-6 inline-flex items-center gap-2 text-[12px] font-700 uppercase tracking-[0.12em] text-[var(--rciq-teal-dark)]">
              Read article
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      data-testid={`article-card-${article.slug}`}
      className="group block bg-white border border-[var(--rciq-line)] rounded-2xl overflow-hidden card-hover animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="aspect-[16/10] bg-[var(--rciq-bg-tint)] overflow-hidden">
        <img
          src={cover}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
        />
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="tag-pill">{article.category}</span>
          <span className="text-xs text-[var(--rciq-muted)]">{formatDate(article.published_at)}</span>
          {(article.reading_time || article.content) && (
            <>
              <span className="text-xs text-[var(--rciq-muted)]">·</span>
              <span className="text-xs text-[var(--rciq-muted)]">
                {article.reading_time || Math.max(1, Math.round(((article.content || article.excerpt || "").trim().split(/\s+/).filter(Boolean).length) / 220))} min read
              </span>
            </>
          )}
        </div>
        <h3 className="font-serif-display text-xl md:text-[1.45rem] leading-[1.2] font-700 text-[var(--rciq-ink)] group-hover:text-[var(--rciq-teal-dark)] transition-colors">
          {article.title}
        </h3>
        {article.excerpt && (
          <p className="mt-3 text-[14.5px] text-[var(--rciq-ink-2)] leading-relaxed line-clamp-3">
            {article.excerpt}
          </p>
        )}
        {Array.isArray(article.tags) && article.tags.length > 0 && (
          <div className="mt-5 flex flex-wrap gap-1.5">
            {article.tags.slice(0, 3).map((t) => (
              <span key={t} className="text-[10.5px] uppercase tracking-[0.1em] text-[var(--rciq-muted)] font-600">
                #{t}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
